import { useEffect, useState, useRef } from "react";
import "./App.css";
import { Mail, Linkedin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    mensaje: ''
  });
  const [formStatus, setFormStatus] = useState('');
  const heroHeadlineRef = useRef(null);

  // Typewriter animation for hero headline
  useEffect(() => {
    const lines = [
      { text: "Primero el problema.", color: "#ffffff" },
      { text: "Después la solución.", color: "#E63946" }
    ];

    const headline = heroHeadlineRef.current;
    if (!headline) return;
    
    // Prevent double execution
    if (headline.dataset.animated === 'true') return;
    headline.dataset.animated = 'true';
    
    headline.innerHTML = '';

    async function typeLine(text, color) {
      const span = document.createElement('span');
      span.style.color = color;
      span.style.display = 'block';
      span.className = 'font-jakarta text-5xl md:text-6xl lg:text-7xl leading-tight';
      span.style.fontWeight = '800';
      headline.appendChild(span);
      
      for (let i = 0; i < text.length; i++) {
        span.textContent += text[i];
        await new Promise(r => setTimeout(r, 40));
      }
    }

    async function startTyping() {
      await typeLine(lines[0].text, lines[0].color);
      await typeLine(lines[1].text, lines[1].color);
    }

    startTyping();
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Animated stats counter
  useEffect(() => {
    const animateNumber = (element, target, suffix, prefix, duration = 2000) => {
      const numberElement = element.querySelector('.stat-number');
      const startTime = performance.now();
      
      const updateNumber = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const current = Math.floor(easeOutQuart * target);
        
        numberElement.textContent = `${prefix}${current}${suffix}`;
        
        if (progress < 1) {
          requestAnimationFrame(updateNumber);
        } else {
          numberElement.textContent = `${prefix}${target}${suffix}`;
        }
      };
      
      requestAnimationFrame(updateNumber);
    };

    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.classList.contains('animated')) {
            entry.target.classList.add('animated');
            const target = parseInt(entry.target.dataset.target);
            const suffix = entry.target.dataset.suffix;
            const prefix = entry.target.dataset.prefix;
            animateNumber(entry.target, target, suffix, prefix);
          }
        });
      },
      { threshold: 0.3 }
    );

    const statElements = document.querySelectorAll('.animated-stat');
    statElements.forEach((el) => statsObserver.observe(el));

    return () => statsObserver.disconnect();
  }, []);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setFormStatus('success');
        setFormData({ nombre: '', empresa: '', email: '', mensaje: '' });
        
        // Clear success message after 10 seconds
        setTimeout(() => setFormStatus(''), 10000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      
      // Clear error message after 8 seconds
      setTimeout(() => setFormStatus(''), 8000);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="nav-fixed">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="#" className="flex items-center">
            <img src="/logo.png" alt="Digital One" style={{ height: '32px', width: 'auto' }} />
          </a>
          
          <div className="desktop-nav hidden md:flex gap-8">
            <a href="#problema" className="nav-link">El Problema</a>
            <a href="#proceso" className="nav-link">Proceso</a>
            <a href="#servicios" className="nav-link">Servicios</a>
            <a href="#resultados" className="nav-link">Resultados</a>
            <a href="#nosotros" className="nav-link">Nosotros</a>
            <a href="#contacto" className="nav-link">Contacto</a>
          </div>

          <div className="hamburger md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <a href="#problema" className="nav-link" onClick={() => setMobileMenuOpen(false)}>El Problema</a>
          <a href="#proceso" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Proceso</a>
          <a href="#servicios" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Servicios</a>
          <a href="#resultados" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Resultados</a>
          <a href="#nosotros" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Nosotros</a>
          <a href="#contacto" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Contacto</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{ background: '#111111', minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: '80px' }}>
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div 
            ref={heroHeadlineRef}
            id="hero-headline"
            style={{ minHeight: '180px', marginBottom: '32px' }}
          >
          </div>
          
          <p className="scroll-animate font-inter text-lg md:text-xl mb-10" style={{ color: '#888888', lineHeight: '1.7' }}>
            Diagnóstico, estrategia y ejecución para empresas que quieren crecer en el ecosistema digital, sin promesas irreales ni cajas negras.
          </p>
          
          <a href="#contacto" className="scroll-animate btn-cta">
            AGENDA UNA CONVERSACIÓN →
          </a>
        </div>
      </section>

      {/* Section 2 - El Problema */}
      <section id="problema" style={{ background: '#1a1a1a', padding: '100px 0' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="scroll-animate section-label">EL PROBLEMA</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            ¿Te suena <span style={{ color: '#E63946' }}>familiar</span>?
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <div className="scroll-animate font-inter text-base md:text-lg space-y-6" style={{ color: '#888888', lineHeight: '1.8' }}>
            <p>
              Llevas meses invirtiendo en digital y los números no cierran. Cambiaste de agencia una o dos veces, cada una llegó con su catálogo, prometió resultados en 30 días y desapareció detrás de reportes que nadie entiende.
            </p>
            <p>
              El 63% de los anunciantes en México no puede rastrear el retorno real de su inversión digital. No es un problema de presupuesto, es un problema de diagnóstico.
            </p>
            <p>
              Tu negocio tiene escala. El dinero existe. Pero nadie te dice con claridad qué está fallando ni cómo arreglarlo.
            </p>
            <p className="font-semibold" style={{ color: '#ffffff' }}>
              Ese es exactamente el problema que resolvemos.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 - Cómo Trabajamos */}
      <section id="proceso" style={{ background: '#111111', padding: '100px 0' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-animate section-label">EL PROCESO</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            Un proceso <span style={{ color: '#E63946' }}>distinto</span> al que ya conoces.
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="scroll-animate">
              <div className="font-inter font-semibold text-xl mb-3" style={{ color: '#E63946' }}>01</div>
              <h3 className="font-jakarta font-bold text-2xl mb-4" style={{ color: '#ffffff' }}>Diagnóstico</h3>
              <p className="font-inter text-base" style={{ color: '#888888', lineHeight: '1.7' }}>
                Antes de proponer cualquier cosa, analizamos qué está pasando realmente en tu ecosistema digital. Sin suposiciones, sin catálogo bajo el brazo.
              </p>
            </div>

            <div className="scroll-animate">
              <div className="font-inter font-semibold text-xl mb-3" style={{ color: '#E63946' }}>02</div>
              <h3 className="font-jakarta font-bold text-2xl mb-4" style={{ color: '#ffffff' }}>Estrategia</h3>
              <p className="font-inter text-base" style={{ color: '#888888', lineHeight: '1.7' }}>
                Con el diagnóstico sobre la mesa, diseñamos un plan a medida. No soluciones genéricas, una hoja de ruta específica para tu negocio, tu mercado y tu momento.
              </p>
            </div>

            <div className="scroll-animate">
              <div className="font-inter font-semibold text-xl mb-3" style={{ color: '#E63946' }}>03</div>
              <h3 className="font-jakarta font-bold text-2xl mb-4" style={{ color: '#ffffff' }}>Ejecución y medición</h3>
              <p className="font-inter text-base" style={{ color: '#888888', lineHeight: '1.7' }}>
                Implementamos, medimos lo que importa y ajustamos. El único indicador que nos interesa: si tu negocio está creciendo o no.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Servicios */}
      <section id="servicios" style={{ background: '#1a1a1a', padding: '100px 0' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-animate section-label">LO QUE HACEMOS</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            Lo que hacemos y <span style={{ color: '#E63946' }}>cómo lo hacemos</span>.
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                number: '01',
                title: 'Diagnóstico Digital',
                description: 'Auditoría del ecosistema digital del negocio: canales, inversión, competencia y oportunidades. El punto de partida de todo lo que sigue.'
              },
              {
                number: '02',
                title: 'Estrategia de Crecimiento Digital',
                description: 'Diseño del plan: qué canales, qué presupuesto, qué métricas. Sin soluciones genéricas, todo calibrado al negocio y al momento.'
              },
              {
                number: '03',
                title: 'Paid Media y Performance',
                description: 'Gestión de campañas en Google, Meta y otros canales. Optimización continua con foco en resultados de negocio, no en métricas de vanidad.'
              },
              {
                number: '04',
                title: 'Presencia Orgánica y Contenido',
                description: 'Redes sociales, email marketing y contenido editorial. Canales que construyen audiencia propia y reducen la dependencia de la pauta paga.'
              },
              {
                number: '05',
                title: 'E-commerce',
                description: 'Estrategia y ejecución para vender en línea: desde la arquitectura del canal hasta la conversión y el ticket promedio.'
              },
              {
                number: '06',
                title: 'CMO Fractional',
                description: 'Para empresas que necesitan dirección estratégica de marketing sin contratar un CMO de tiempo completo. Sebastián opera como director externo con agenda y accountability reales.'
              }
            ].map((service, index) => (
              <div key={index} className="scroll-animate service-card">
                <div className="service-number">{service.number}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
            
            {/* Last row with 2 cards centered */}
            <div className="scroll-animate service-card md:col-start-1">
              <div className="service-number">07</div>
              <h3 className="service-title">Automatización e Inteligencia Aplicada</h3>
              <p className="service-description">Flujos automatizados, integración de herramientas y agentes conversacionales que reducen fricción en el proceso de compra. Sin hype, solo lo que mueve el negocio.</p>
            </div>

            <div className="scroll-animate service-card md:col-start-3 flex flex-col justify-between">
              <div>
                <h3 className="service-title mb-4">¿No encuentras lo que buscas?</h3>
                <p className="service-description mb-6">Cada negocio tiene un desafío distinto. Cuéntanos qué está pasando y lo resolvemos juntos.</p>
              </div>
              <a href="#contacto" className="btn-cta text-center" style={{ fontSize: '13px', padding: '14px 24px' }}>
                HABLEMOS →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 - Resultados */}
      <section id="resultados" style={{ background: '#111111', padding: '100px 0' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-animate section-label">RESULTADOS</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            Números reales. <span style={{ color: '#E63946' }}>Sin humo</span>.
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            <div className="animated-stat" data-target="40" data-suffix="x" data-prefix="">
              <div className="stat-number font-jakarta" style={{ color: '#E63946', fontSize: '56px', fontWeight: 800, lineHeight: '1.2', marginBottom: '16px' }}>0x</div>
              <div className="font-inter" style={{ color: '#666666', fontSize: '16px', fontWeight: 400, marginBottom: '8px' }}>Conversiones · Fintech</div>
              <div className="font-inter" style={{ color: '#444444', fontSize: '14px', fontWeight: 400 }}>Optimización de funnel + paid media</div>
            </div>

            <div className="animated-stat" data-target="92" data-suffix="%" data-prefix="+">
              <div className="stat-number font-jakarta" style={{ color: '#E63946', fontSize: '56px', fontWeight: 800, lineHeight: '1.2', marginBottom: '16px' }}>+0%</div>
              <div className="font-inter" style={{ color: '#666666', fontSize: '16px', fontWeight: 400, marginBottom: '8px' }}>Tráfico orgánico · E-commerce</div>
              <div className="font-inter" style={{ color: '#444444', fontSize: '14px', fontWeight: 400 }}>Audio Premium MX</div>
            </div>

            <div className="animated-stat" data-target="71" data-suffix="%" data-prefix="-">
              <div className="stat-number font-jakarta" style={{ color: '#E63946', fontSize: '56px', fontWeight: 800, lineHeight: '1.2', marginBottom: '16px' }}>-0%</div>
              <div className="font-inter" style={{ color: '#666666', fontSize: '16px', fontWeight: 400, marginBottom: '8px' }}>Reducción de CPA · Seguros</div>
              <div className="font-inter" style={{ color: '#444444', fontSize: '14px', fontWeight: 400 }}>Estrategia integral en 12 meses</div>
            </div>

            <div className="animated-stat" data-target="23" data-suffix="k" data-prefix="+">
              <div className="stat-number font-jakarta" style={{ color: '#E63946', fontSize: '56px', fontWeight: 800, lineHeight: '1.2', marginBottom: '16px' }}>+0k</div>
              <div className="font-inter" style={{ color: '#666666', fontSize: '16px', fontWeight: 400, marginBottom: '8px' }}>Transacciones · Retail</div>
              <div className="font-inter" style={{ color: '#444444', fontSize: '14px', fontWeight: 400 }}>Email marketing + paid media</div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 - Quiénes Somos */}
      <section id="nosotros" style={{ background: '#1a1a1a', padding: '100px 0' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="scroll-animate section-label">QUIÉNES SOMOS</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            Una firma. Un <span style={{ color: '#E63946' }}>método</span>. +20 años en Digital.
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <div className="scroll-animate font-inter text-base md:text-lg space-y-6 mt-8" style={{ color: '#888888', lineHeight: '1.8' }}>
            <p>
              Digital One es una firma de servicios digitales con base en México y operación en Latinoamérica. Estrategia, ejecución y tecnología aplicada al crecimiento para empresas que ya tienen escala y necesitan que su inversión digital les responda.
            </p>
            <p>
              La firma es dirigida por Sebastián Moglia Claps, con más de 20 años de experiencia en el ecosistema digital de México y LatAm. Columnista en Forbes México, conferencista internacional y docente en IAB y AMVO.
            </p>
          </div>
        </div>
      </section>

      {/* Section 7 - Contacto */}
      <section id="contacto" style={{ background: '#111111', padding: '100px 0' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="scroll-animate section-label">CONTACTO</div>
          <h2 className="scroll-animate font-jakarta font-bold text-4xl md:text-5xl mb-4" style={{ color: '#ffffff' }}>
            Hablemos de tu <span style={{ color: '#E63946' }}>negocio</span>.
          </h2>
          <div className="scroll-animate accent-line"></div>
          
          <p className="scroll-animate font-inter text-base md:text-lg mb-10" style={{ color: '#888888', lineHeight: '1.7' }}>
            Sin formularios largos ni llamadas de descubrimiento de 45 minutos. Cuéntanos qué está pasando — en 15 minutos sabemos si podemos ayudarte.
          </p>

          <form onSubmit={handleFormSubmit} className="scroll-animate">
            <input
              type="text"
              name="nombre"
              placeholder="Nombre"
              value={formData.nombre}
              onChange={handleFormChange}
              className="form-input"
              required
            />
            
            <input
              type="text"
              name="empresa"
              placeholder="Empresa"
              value={formData.empresa}
              onChange={handleFormChange}
              className="form-input"
              required
            />
            
            <input
              type="email"
              name="email"
              placeholder="Correo electrónico"
              value={formData.email}
              onChange={handleFormChange}
              className="form-input"
              required
            />
            
            <textarea
              name="mensaje"
              placeholder="¿Qué está pasando en tu negocio?"
              value={formData.mensaje}
              onChange={handleFormChange}
              className="form-textarea"
              required
            ></textarea>
            
            <button type="submit" className="btn-cta w-full" disabled={formStatus === 'sending'}>
              {formStatus === 'sending' ? 'ENVIANDO...' : 'ENVIAR'}
            </button>

            {formStatus === 'success' && (
              <div className="mt-6 p-4 font-inter text-sm" style={{ background: '#1a1a1a', color: '#4ade80', border: '1px solid #22c55e', borderRadius: '4px' }}>
                ✓ Muchas gracias por contactarnos, pronto nos pondremos en contacto contigo para platicar de tu proyecto.
              </div>
            )}
            
            {formStatus === 'error' && (
              <div className="mt-6 p-4 font-inter text-sm" style={{ background: '#1a1a1a', color: '#f87171', border: '1px solid #ef4444', borderRadius: '4px' }}>
                ✗ Hubo un error al enviar tu mensaje. Por favor, intenta de nuevo o contáctanos directamente por email.
              </div>
            )}
          </form>

          <div className="scroll-animate flex gap-6 justify-center mt-12">
            <a 
              href="https://linkedin.com/in/sebastianmoglia"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-icon"
            >
              <Linkedin size={28} />
            </a>
            
            <a 
  href="mailto:contacto@digitalone.com.mx"
  className="contact-icon"
  style={{ fontSize: '14px', fontFamily: 'Inter', letterSpacing: '0.5px' }}
>
  contacto@digitalone.com.mx
</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        DIGITAL ONE · © 2025 · Todos los derechos reservados
      </footer>
    </div>
  );
}

export default App;
